import React from 'react';
import { Box, Typography } from '@mui/material';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import { PEDIGREE } from 'src/components/Constants';
import { analyzeHide } from '../../pedigree/hideGuard';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// Phase 1.5 of PLAN-pedigree-undo-redo-and-saved-views (v1.1 §3.5) — the
// hide-connector guard. Hiding used to apply unconditionally: hiding a
// structural connector (e.g. the parent bridging a branch to the grandparents)
// silently fragmented the chart, leaving relatives floating with no drawn
// connection, and the fragmented state persisted through the people-save.
// Now: a hide that disconnects nobody applies immediately (behavior unchanged);
// a hide that WOULD orphan relatives confirms first and hides the whole
// disconnected branch together, so the visible chart stays one coherent tree.
// Anyone hidden this way is individually restorable from the Hidden Members
// panel, exactly as before.
function HidePerson({
  node,
  memberRecords,
  setMemberRecords,
  handleClose,
  setPedigreeState,
  // The DRAWN member set — ChartBox's deriveProxyNodes output, the exact array
  // the canvas renders. The guard must analyse what the clinician SEES: an
  // active proxy SEVERS the original's drawn marriage/children (they hang off
  // the clone instead), so analysing raw memberRecords would keep phantom
  // edges and declare a fragmenting hide "safe".
  analysisMembers = null,
  // SAVED VIEWS (Phase 2, plan §3.5): while a view is active, hide edits the
  // view's render-time OVERLAY instead of the person's shared `hidden` flag —
  // `hide(pids)` lands the gesture in the overlay. Null when on Standard.
  viewHide = null
}) {
  const t = useAutoTranslation();

  // Pure, synchronous connectivity analysis of the CURRENT drawn chart —
  // computed once for this menu invocation. Proxy CLONES are render artifacts,
  // never hideable people: strip them from the orphan report (hiding their
  // real cluster-mates removes the clone's reason to render).
  const analysis = React.useMemo(
    () => {
      const members = analysisMembers ?? viewHide?.members ?? memberRecords;
      const result = analyzeHide(members, node.pid);
      const isClone = (pid) =>
        members.find((m) => m.pid === pid)?.isProxy === true;
      return {
        orphanedPids: result.orphanedPids.filter((pid) => !isClone(pid))
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const applyHide = (pids) => {
    if (viewHide) {
      // Overlay edit only — the shared people records (and the people-save)
      // are untouched by construction.
      viewHide.hide(pids);
      handleClose();
      return;
    }
    const hideSet = new Set(pids);
    setMemberRecords((prev) =>
      prev.map((p) => (hideSet.has(p.pid) ? { ...p, hidden: true } : p))
    );
    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));
    handleClose();
  };

  const isSafe = analysis.orphanedPids.length === 0;

  React.useEffect(() => {
    if (isSafe) applyHide([node.pid]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isSafe) return null;

  const orphans = analysis.orphanedPids.map(
    (pid) => memberRecords.find((m) => m.pid === pid) || { pid }
  );
  const displayName = (m) => {
    const name = [m.firstName, m.lastName].filter(Boolean).join(' ');
    const id = m.upn || m.pid;
    return name
      ? t('{{name}} (UPN {{id}})', { name, id })
      : t('UPN {{id}}', { id });
  };
  const shown = orphans.slice(0, 8);
  const extra = orphans.length - shown.length;
  const total = orphans.length + 1;

  return (
    <CustomDialog
      open={true}
      handleClose={handleClose}
      showActions={true}
      saveBtnLabel="Hide all"
      cancelBtnLabel="Cancel"
      title="Hiding will disconnect relatives"
      titleAppearance="warning"
      minHeight={200}
      // async: CustomDialog's save handler calls onSave().then(...) — a bare
      // undefined return throws on every confirm (the review's blocker).
      onSave={async () => applyHide([node.pid, ...analysis.orphanedPids])}
      allowBackgroundClose={handleClose}
    >
      <Box>
        <Typography
          sx={{
            fontWeight: 'bold'
          }}
        >
          {orphans.length === 1
            ? t('{{name}} connects a relative to the rest of the pedigree.', {
                name: displayName(node)
              })
            : t(
                '{{name}} connects {{count}} relatives to the rest of the pedigree.',
                { name: displayName(node), count: orphans.length }
              )}
        </Typography>
        <Typography
          sx={{
            pt: 1
          }}
        >
          {t(
            'Hiding only this person would leave the following with no drawn\n          connection to the chart, so all {{total}} will be hidden together:',
            { total }
          )}
        </Typography>
        <Box component="ul" sx={{ my: 1.5, pl: 3 }}>
          {shown.map((m) => (
            <li key={m.pid}>
              <Typography component="span">{displayName(m)}</Typography>
            </li>
          ))}
          {extra > 0 && (
            <li>
              <Typography component="span">
                {t('…and {{extra}} more', { extra })}
              </Typography>
            </li>
          )}
        </Box>
        <Typography variant="subtitle2">
          {t(
            'Nothing is deleted — anyone hidden here can be restored individually from the Hidden Members panel.'
          )}
        </Typography>
      </Box>
    </CustomDialog>
  );
}

export default HidePerson;
